import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";
import Icon from "components/AppIcon";
import { base_url } from "Backend/api";
import toast from "react-hot-toast";
import {
  markOrderRated,
  markRateDismissed,
} from "utils/orderReviewStorage";
import { resolveMediaUrl } from "utils/resolveMediaUrl";
import AppSpinner from "components/ui/AppSpinner";
import rateSuccessAnimation from "../../../public/assets/lottie/rateSuccess.json";

const SUGGESTIONS = [
  "الأكل ممتاز",
  "التوصيل سريع",
  "الأسعار مناسبة",
  "جودة عالية",
  "تجربة رائعة",
];

const DIMENSION_LABEL_AR = {
  rating_service: "الخدمة",
  rating_food: "الطعم",
  rating_taste: "الطعم",
  rating_delivery: "خدمة التوصيل",
  rating_products: "المنتجات",
  rating_prices: "الأسعار",
};

function dimensionLabel(dim) {
  if (!dim) return "";
  return (
    dim.label ||
    dim.label_ar ||
    dim.name ||
    DIMENSION_LABEL_AR[dim.key] ||
    dim.key ||
    ""
  );
}

function storeLogoSrc(store) {
  const raw =
    store.logo_url ||
    store.avatar ||
    store.cover_image ||
    store.logo ||
    store.image ||
    store.photo ||
    store?.media?.logo ||
    store?.media?.main_image ||
    store?.media?.cover_image ||
    store?.media?.image;
  return resolveMediaUrl(raw);
}

function StarRow({ value, onChange }) {
  const display = value > 0 ? Number(value).toFixed(1) : "0.0";
  const [hover, setHover] = useState(null);
  const active = hover ?? value;

  return (
    <div className="flex flex-row-reverse items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(null)}
            className="p-0.5 rounded"
            aria-label={`${n} stars`}
          >
            <Icon
              name="Star"
              size={22}
              className={
                n <= active
                  ? "fill-amber-400 text-amber-400"
                  : "text-gray-300"
              }
            />
          </button>
        ))}
      </div>
      <span className="min-w-[2.25rem] text-left text-sm font-semibold text-gray-700">
        {display}
      </span>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.orderSlug
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSuccess]
 */
export default function RateOrderModal({
  orderSlug,
  open,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [resolvedSlug, setResolvedSlug] = useState("");
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!open || !orderSlug || !token) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setOrderData(null);
      try {
        const enc = encodeURIComponent(orderSlug);
        const res = await fetch(`${base_url}/api/orders/${enc}/get-review`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.status) {
          toast.error(json.message || "تعذر تحميل بيانات التقييم");
          if (!cancelled) onClose?.();
          return;
        }
        const data = json.data || {};
        const slug =
          data?.order?.slug || data?.order_slug || orderSlug || "";
        const stores = Array.isArray(data.stores) ? data.stores : [];
        if (!cancelled) {
          setOrderData(data);
          setResolvedSlug(slug);
          const nextRatings = {};
          const nextComments = {};
          stores.forEach((store) => {
            const sid = store.store_id;
            nextRatings[sid] = {};
            nextComments[sid] = "";
            (store.dimensions || []).forEach((dim) => {
              nextRatings[sid][dim.key] = 0;
            });
          });
          setRatings(nextRatings);
          setComments(nextComments);
        }
      } catch (e) {
        console.error(e);
        toast.error("حدث خطأ في الاتصال");
        if (!cancelled) onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, orderSlug, token]);

  useEffect(() => {
    if (!showSuccessDialog) return undefined;

    const timeoutId = window.setTimeout(() => {
      setShowSuccessDialog(false);
      onClose?.();
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [showSuccessDialog, onClose]);

  const updateRating = (storeId, key, val) => {
    setRatings((prev) => ({
      ...prev,
      [storeId]: { ...(prev[storeId] || {}), [key]: val },
    }));
  };

  const appendSuggestion = (storeId, text) => {
    setComments((prev) => {
      const cur = (prev[storeId] || "").trim();
      if (cur.includes(text)) return prev;
      const merged = cur ? `${cur}، ${text}` : text;
      return { ...prev, [storeId]: merged };
    });
  };

  const handleDismiss = () => {
    const s = resolvedSlug || orderSlug;
    markRateDismissed(s);
    onClose?.();
  };

  const submitReview = async () => {
    const stores = Array.isArray(orderData?.stores) ? orderData.stores : [];
    const storeReviews = [];

    for (const store of stores) {
      const storeId = store.store_id;
      const storeType = store.store_type;
      const comment = (comments[storeId] || "").trim();
      const hasComment = comment.length > 0;

      const review = {
        store_type: storeType,
        store_id: storeId,
      };

      const dimensions = Array.isArray(store.dimensions) ? store.dimensions : [];
      let hasRating = false;

      dimensions.forEach((dim) => {
        const key = dim.key;
        const value = ratings[storeId]?.[key] ?? 0;
        if (value > 0) {
          review[key] = value;
          hasRating = true;
        }
      });

      if (hasComment) {
        review.comment = comment;
      }

      if (hasRating || hasComment) {
        storeReviews.push(review);
      }
    }

    if (storeReviews.length === 0) {
      toast.error("من فضلك قيم على الأقل عنصر واحد");
      return;
    }

    const slug = resolvedSlug || orderSlug;
    const body =
      storeReviews.length > 1
        ? { store_reviews: storeReviews }
        : { store_review: storeReviews[0] };

    setSubmitting(true);
    try {
      const enc = encodeURIComponent(slug);
      const res = await fetch(`${base_url}/api/orders/${enc}/review`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.status === false) {
        toast.error(json.message || "فشل إرسال التقييم");
        return;
      }
      markOrderRated(slug);
      onSuccess?.();
      setShowSuccessDialog(true);
    } catch (e) {
      console.error(e);
      toast.error("فشل إرسال التقييم");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const stores = Array.isArray(orderData?.stores) ? orderData.stores : [];

  const successDialog = (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/45 p-4 font-[cairo]"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rate-success-title"
    >
      <div className="w-full max-w-[320px] rounded-[20px] bg-white px-3 py-4 text-center shadow-2xl sm:max-w-[360px] sm:px-4 sm:py-5">
        <Lottie
          animationData={rateSuccessAnimation}
          loop={false}
          className="mx-auto mb-1 h-[62px] w-[190px] sm:h-[76px] sm:w-[220px]"
        />
        <p
          id="rate-success-title"
          className="text-[18px] font-extrabold leading-tight text-[#1F2937] sm:text-[22px]"
        >
          شكراً على تقييم الطلب!
        </p>
      </div>
    </div>
  );

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-3 font-[cairo]"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rate-order-title"
    >
      <div className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-[8px] bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
            aria-label="إغلاق"
          >
            <Icon name="X" size={22} />
          </button>
          <h2
            id="rate-order-title"
            className="text-base font-bold text-gray-900 font-[cairo]"
          >
            تقييم الطلب
          </h2>
          <span className="w-8" />
        </div>

        <div className="px-4 pb-5 pt-3">
          {loading && (
            <div className="flex justify-center py-16">
              <AppSpinner />
            </div>
          )}

          {!loading &&
            stores.map((store) => {
              const sid = store.store_id;
              const name =
                store.name ||
                store.store_name ||
                store.title ||
                "المتجر";
              const reviewsTotal =
                store.reviews_count ??
                store.rating?.reviews_count ??
                store.reviews?.total ??
                0;
              const logo = storeLogoSrc(store);
              const dimensions = Array.isArray(store.dimensions)
                ? store.dimensions
                : [];

              return (
                <div
                  key={`${store.store_type}-${sid}`}
                  className="mb-6 rounded-[6px] border border-gray-100 bg-gray-50/80 p-4 last:mb-2"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border border-gray-200 bg-white">
                      {logo ? (
                        <img
                          src={logo}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-orange-50">
                          <Icon
                            name="Store"
                            className="text-orange-500"
                            size={28}
                          />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 text-right">
                      <div className="truncate text-base font-bold text-gray-900">
                        {name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {reviewsTotal} تقييم
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {dimensions.map((dim) => (
                      <div
                        key={dim.key}
                        className="flex flex-col gap-1 rounded-[6px] bg-white p-3 shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-800">
                            {dimensionLabel(dim)}
                          </span>
                          <StarRow
                            value={ratings[sid]?.[dim.key] ?? 0}
                            onChange={(v) => updateRating(sid, dim.key, v)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <div className="mb-2 text-sm font-semibold text-gray-800">
                      تعليقك
                    </div>
                    <div className="mb-2 flex flex-wrap gap-2">
                      {SUGGESTIONS.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => appendSuggestion(sid, t)}
                          className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 transition hover:border-[#F93209] hover:text-[#F93209]"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={comments[sid] ?? ""}
                      onChange={(e) =>
                        setComments((prev) => ({
                          ...prev,
                          [sid]: e.target.value,
                        }))
                      }
                      rows={4}
                      placeholder="يمكنك كتابة تعليقك حول الطلب هنا أو اختيار أحد الاقتراحات..."
                      className="w-full resize-none rounded-[6px] border border-gray-200 p-3 text-sm text-gray-800 outline-none ring-[#F93209] focus:border-[#F93209] focus:ring-1"
                    />
                  </div>
                </div>
              );
            })}

          {!loading && stores.length === 0 && (
            <p className="py-8 text-center text-gray-500">
              لا يوجد متاجر لتقييمها لهذا الطلب.
            </p>
          )}
        </div>

        {!loading && stores.length > 0 && (
          <div className="sticky bottom-0 border-t border-gray-100 bg-white px-4 pb-4 pt-3">
            <button
              type="button"
              disabled={submitting}
              onClick={submitReview}
              className="w-full rounded-[6px] bg-[#F93209] py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-[#e02d08] disabled:opacity-60"
            >
              {submitting ? "جارِ الإرسال..." : "إرسال التقييم"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(showSuccessDialog ? successDialog : modal, document.body);
}
